import {
  Button,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  ActiveSearchContext,
  SearchContext,
  isChannelContext,
} from "../types";

const CONTEXT_LABELS: Record<SearchContext, string> = {
  all: "All",
  series: "Series",
  releases: "Releases",
  creators: "Creators",
  bundles: "Bundles",
};

// On browse: no "All" (tabs handle that). Off browse: include "All".
const BROWSE_CONTEXTS: SearchContext[] = ["series", "releases", "creators", "bundles"];
const OFF_BROWSE_CONTEXTS: SearchContext[] = ["all", "series", "releases", "creators", "bundles"];

interface SearchContextSelectorProps {
  context: ActiveSearchContext;
  channelIcon?: string;
  channelDisplayName?: string;
  isOnChannelPage?: boolean;
  isOnBrowsePage?: boolean;
  onSelect: (ctx: SearchContext) => void;
  onSelectChannel?: () => void;
}

export function SearchContextSelector({
  context,
  channelIcon,
  channelDisplayName,
  isOnChannelPage,
  isOnBrowsePage,
  onSelect,
  onSelectChannel,
}: SearchContextSelectorProps) {
  const isChannel = isChannelContext(context);
  const label = isChannel
    ? (channelDisplayName ?? context.browseKey)
    : CONTEXT_LABELS[context];
  const selectableContexts = isOnBrowsePage ? BROWSE_CONTEXTS : OFF_BROWSE_CONTEXTS;

  return (
    <Menu placement="bottom-start">
      <MenuButton
        as={Button}
        variant="searchContext"
        rightIcon={<ChevronDownIcon boxSize="24px" />}
        leftIcon={
          isChannel && channelIcon ? (
            <Image src={channelIcon} alt="" boxSize="24px" objectFit="contain" />
          ) : undefined
        }
        flexShrink={0}
        fontSize="md"
        iconSpacing="4px"
      >
        {label}
      </MenuButton>
      <MenuList
        bg="transparent.black.90"
        backdropFilter="blur(36px)"
        border="1px solid"
        borderColor="transparent.white.20"
        borderRadius="16px"
        minW="150px"
        p="7px"
        boxShadow="0px 4px 24px 0px rgba(0,0,0,0.4)"
        zIndex="popover"
        motionProps={{
          variants: {
            enter: {
              visibility: "visible",
              opacity: 1,
              y: 0,
              transition: { duration: 0.15, ease: [0.4, 0, 0.2, 1] },
            },
            exit: {
              transitionEnd: { visibility: "hidden" },
              opacity: 0,
              y: -8,
              transition: { duration: 0.1, ease: "easeOut" },
            },
          },
        }}
      >
        {selectableContexts.map((ctx) => {
          const isActive = !isChannel && context === ctx;
          return (
            <MenuItem
              key={ctx}
              bg={isActive ? "transparent.white.10" : "transparent"}
              color={isActive ? "dune.100" : "dune.700"}
              fontFamily="Roboto"
              fontWeight="400"
              fontSize="14px"
              letterSpacing="0.5px"
              borderRadius="16px"
              h="40px"
              _hover={{ bg: "transparent.white.10", color: "dune.100" }}
              onClick={() => onSelect(ctx)}
            >
              {CONTEXT_LABELS[ctx]}
            </MenuItem>
          );
        })}
        {isOnChannelPage && channelDisplayName && (
          <MenuItem
            bg={isChannel ? "transparent.white.10" : "transparent"}
            color={isChannel ? "dune.100" : "dune.700"}
            fontFamily="Roboto"
            fontWeight="400"
            fontSize="14px"
            letterSpacing="0.5px"
            borderRadius="16px"
            h="40px"
            _hover={{ bg: "transparent.white.10", color: "dune.100" }}
            onClick={() => onSelectChannel?.()}
            icon={
              channelIcon ? (
                <Image src={channelIcon} alt="" boxSize="20px" objectFit="contain" />
              ) : undefined
            }
          >
            {channelDisplayName}
          </MenuItem>
        )}
      </MenuList>
    </Menu>
  );
}
